<script>
    import { slide } from 'svelte/transition';
    import { createEventDispatcher } from 'svelte';

    export let title = '';
    export let stepNumber = 1;
    export let isOpen = false;
    export let isCompleted = false;
    export let isLocked = false;
    export let subtitle = '';
    /** Show an "Edit" affordance instead of a chevron once the step is done */
    export let editable = false;

    const dispatch = createEventDispatcher();

    $: showEdit = editable && isCompleted && !isOpen && !isLocked;

    function handleToggle() {
        if (isLocked) return;
        dispatch('toggle');
    }
</script>

<div class="accordion-section" class:open={isOpen} class:completed={isCompleted} class:locked={isLocked}>
    <div class="accordion-header">
        <button
            type="button"
            class="accordion-trigger"
            on:click={handleToggle}
            disabled={isLocked}
            aria-expanded={isOpen}
        >
            <div class="step-badge" class:completed={isCompleted} class:active={isOpen && !isCompleted}>
                {#if isCompleted}
                    <i class="fa-solid fa-check"></i>
                {:else}
                    {stepNumber}
                {/if}
            </div>
            <div class="accordion-title-group">
                <span class="accordion-title">{title}</span>
                {#if !isOpen}
                    <slot name="collapsedSummary">
                        {#if subtitle}
                            <span class="accordion-subtitle">{subtitle}</span>
                        {/if}
                    </slot>
                {/if}
            </div>
        </button>
        <div class="accordion-header-right">
            {#if isLocked}
                <i class="fa-solid fa-lock text-gray-400"></i>
            {:else if showEdit}
                <button type="button" class="accordion-edit" on:click={handleToggle}>Edit</button>
            {:else}
                <i class="fa-solid fa-chevron-down accordion-chevron" class:rotated={isOpen}></i>
            {/if}
        </div>
    </div>

    {#if isOpen}
        <div class="accordion-body" transition:slide={{ duration: 300 }}>
            <slot />
        </div>
    {/if}
</div>

<style lang="scss">
    .accordion-section {
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        overflow: hidden;
        background: white;
        position: relative;
        transition: box-shadow 0.2s ease;

        &.open {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
            // Lets the address autocomplete dropdown escape the card and sit
            // above the steps that follow it.
            overflow: visible;
            z-index: 1;
        }

        &.locked {
            opacity: 0.6;
        }
    }

    .accordion-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 16px 20px;
        gap: 12px;
    }

    .accordion-trigger {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        text-align: left;

        &:disabled {
            cursor: not-allowed;
        }
    }

    .accordion-edit {
        background: none;
        border: none;
        padding: 0;
        font-size: 13px;
        font-weight: 500;
        color: var(--secondary-color, #2196c9);
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .step-badge {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 14px;
        flex-shrink: 0;
        background: #e5e7eb;
        color: #6b7280;
        transition: all 0.2s ease;

        &.active {
            background: var(--secondary-color, #232D40);
            color: white;
        }

        &.completed {
            background: #10b981;
            color: white;
        }
    }

    .accordion-title-group {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    .accordion-title {
        font-weight: 600;
        font-size: 16px;
        color: #1f2937;
    }

    .accordion-subtitle {
        font-size: 13px;
        color: #6b7280;
    }

    .accordion-header-right {
        flex-shrink: 0;
    }

    .accordion-chevron {
        transition: transform 0.3s ease;
        color: #9ca3af;

        &.rotated {
            transform: rotate(180deg);
        }
    }

    .accordion-body {
        padding: 0 20px 20px;
    }
</style>
